import { motion } from "framer-motion";
import { MessageCircle, Info } from "lucide-react";
import { MenuItem, MenuCategory, RESTAURANT_CONSTANTS } from "@/lib/index";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { springPresets, hoverLift, fadeInUp } from "@/lib/motion";

interface MenuItemCardProps {
  item: MenuItem;
}

export function MenuItemCard({ item }: MenuItemCardProps) {
  const whatsappMessage = encodeURIComponent(`Bonjour, je souhaiterais commander le plat suivant : ${item.name} (${item.price}€)`);
  const orderLink = `${RESTAURANT_CONSTANTS.WHATSAPP_LINK}?text=${whatsappMessage}`;

  return (
    <motion.div
      variants={hoverLift}
      initial="rest"
      whileHover="hover"
      className="h-full"
    >
      <Card className="overflow-hidden h-full flex flex-col border-border/40 bg-card/50 backdrop-blur-sm transition-colors hover:border-primary/30 group">
        {item.image && (
          <div className="aspect-[16/10] overflow-hidden relative">
            <img
              src={item.image}
              alt={item.name}
              className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-110"
            />
            {item.isChildMenu && (
              <Badge className="absolute top-3 right-3 bg-accent text-accent-foreground border-none">
                Menu Enfant
              </Badge>
            )}
          </div>
        )}

        <CardHeader className="p-5 pb-2">
          <div className="flex justify-between items-start gap-2">
            <CardTitle className="text-lg font-semibold leading-tight text-primary">
              {item.name}
            </CardTitle>
            <span className="font-mono font-bold text-accent text-lg shrink-0">
              {item.price.toFixed(2)}€
            </span>
          </div>
        </CardHeader>

        <CardContent className="p-5 pt-0 flex-grow">
          {item.description ? (
            <p className="text-muted-foreground text-sm leading-relaxed">
              {item.description}
            </p>
          ) : (
            <div className="flex items-center gap-2 text-muted-foreground/60 italic text-sm">
              <Info className="w-4 h-4" />
              Plat préparé à la commande
            </div>
          )}
        </CardContent>

        <CardFooter className="p-5 pt-0">
          <Button
            asChild
            variant="default"
            className="w-full bg-accent hover:bg-accent/90 text-accent-foreground font-semibold rounded-full transition-all active:scale-95 shadow-lg shadow-accent/20"
          >
            <a href={orderLink} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2">
              <MessageCircle className="w-4 h-4" />
              Commander via WhatsApp
            </a>
          </Button>
        </CardFooter>
      </Card>
    </motion.div>
  );
}

interface MenuCategorySectionProps {
  category: MenuCategory;
}

export function MenuCategorySection({ category }: MenuCategorySectionProps) {
  return (
    <motion.section
      variants={fadeInUp}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-50px" }}
      transition={springPresets.gentle}
      className="mb-16"
      id={category.id}
    >
      <div className="flex items-center gap-4 mb-8">
        <h2 className="text-3xl font-bold text-primary tracking-tight whitespace-nowrap">
          {category.title}
        </h2>
        <div className="h-px w-full bg-gradient-to-r from-border/80 to-transparent" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
        {category.items.map((item) => (
          <MenuItemCard key={item.id} item={item} />
        ))}
      </div>
    </motion.section>
  );
}
